import Funnel from '@/components/funnel'
import { useQuery } from '@apollo/client'
import ManualBudget from './ManualBudget'
import AiAudienceBudgetNote from './AiAudienceBudgetNote'
import BudgetMoveSummary from './BudgetMoveSummary'
import AdvantageLeadsBudget, { describeCampaignBudgetProblem } from './AdvantageLeadsBudget'
import CampaignBudgetSection, { describeSectionProblem } from './CampaignBudgetSection'
import SmartBudgeting from './SmartBudget'
import { Label } from '@/components/ui/label'
import createBudgetStore from '@/store/budgetStore'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { InfoIcon } from '@/components/icons/InfoIcon'
import { showNotification } from '@/utils/notifications'
import { formatCurrencyAutoLocale } from '@/utils/tools'
import { GET_AUDIENCES } from '@/graphql/audience-queries'
import { useAudienceDataWithHypotheses } from '@/hooks/useAudiencesWithHypotheses'
import { useWizard } from '@/components/common/wizard/WizardContext'
import { useMediaBuyingContext } from '@/context/media-buying-context'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { calculateBudgetDistribution } from '@/utils/ad-campaigns-helpers'
import { getAllStages, getAvailableStages, isAudienceExcluded } from '@/utils/ad-campaigns-helpers'
import { ALL_STAGES } from '@/utils/ad-campaign-constants'
import {
    getBudgetMove,
    hasSelectedAiAudiences,
    manualBudgetsAreWhole,
    manualBudgetTotal,
    splitBudget,
    wholeUnitOf,
} from '@/utils/budget-move'
import { useParams } from 'react-router-dom'
import { ADVANTAGE_LEADS_OPTIONS } from '@/graphql/advantage-leads-queries'
import { CAMPAIGN_BUDGET_OPTIONS } from '@/graphql/campaign-budget-queries'
import {
    ADVANTAGE_LEADS_BUDGET_TYPE,
    ADVANTAGE_LEADS_LABEL,
    campaignBudgetShare,
    countAudiences,
} from '@/utils/advantage-leads'
import {
    CAMPAIGN_BUDGET_DESCRIPTION,
    CAMPAIGN_BUDGET_EXISTING_NOTE,
    CAMPAIGN_BUDGET_LABEL,
    CAMPAIGN_BUDGET_TYPE,
    limitKey,
    limitToMinor,
    plainCampaignBudgetShare,
} from '@/utils/campaign-budget'
import { toMinorUnits, parseBudgetAmount } from '@/utils/audience-builder'
import {
    WARMUP_OFFERED_STEP,
    WARMUP_STEP,
    defaultWarmupChoice,
    describeWarmupProblem,
    isWarmupObjective,
} from '@/utils/pixel-warmup'
import WarmupSection from './WarmupSection'

import _ from 'lodash'

const BudgetSchedule = ({ isAddAudience = false }) => {
    const { getStepContentData, setStepContentData, registerNextGuard, deregisterNextGuard } = useWizard()
    const selectFormatData = getStepContentData('campaignObjective') || {}
    // Get allowed audiences from sales funnel setup
    const [selectedObjective] = useState(selectFormatData.selectedObjective)
    const pixelIsActive = !!selectFormatData?.pixelIsValid
    const availableStages = getAvailableStages(selectedObjective)
    const audienceSelection = getStepContentData('salesFunnelSetup')?.audienceSelections || {}
    const mediaBuyingContext = useMediaBuyingContext()
    const { connectedPublishers } = mediaBuyingContext
    const publisher = connectedPublishers ? Object.values(connectedPublishers)[0] : null
    const { currency, min_daily_budget: minDailyBudget } = publisher
    const formattedMinAmount = formatCurrencyAutoLocale({ amount: minDailyBudget / 100, currency })
    // AI audiences added to an existing campaign are funded by an amount moved from one of its ad sets
    const salesFunnelData = getStepContentData('salesFunnelSetup')
    const budgetMove = useMemo(
        () => (isAddAudience && hasSelectedAiAudiences(salesFunnelData) ? getBudgetMove(salesFunnelData) : null),
        [isAddAudience, salesFunnelData],
    )

    // One Advantage+ leads campaign budget: offered by the server for new lead campaigns only
    const { id: campaignId } = useParams()
    const prefetchedData = getStepContentData('prefetchedData') || {}
    const isEditMode = prefetchedData.isEditMode === true || prefetchedData.isEditAdSetMode === true
    const addToAdvantageLeads = isAddAudience && prefetchedData.campaignBudgetMode === ADVANTAGE_LEADS_BUDGET_TYPE
    const addToPlainBudget = isAddAudience && prefetchedData.campaignBudgetMode === CAMPAIGN_BUDGET_TYPE
    const addToCampaignBudget = addToAdvantageLeads || addToPlainBudget
    const publisherId = connectedPublishers ? Object.keys(connectedPublishers)[0] : undefined
    const objectiveKey = String(selectedObjective || '').toLowerCase()
    const { data: advantageLeadsData } = useQuery(ADVANTAGE_LEADS_OPTIONS, {
        variables: { campaignId, publisherId, objective: objectiveKey },
        skip: !campaignId || !publisherId || isAddAudience || isEditMode || objectiveKey !== 'leads',
        fetchPolicy: 'network-only',
    })
    const advantageLeadsOptions = advantageLeadsData?.AdCampaign?.advantageLeadsOptions || null
    const advantageLeadsOffered = !!advantageLeadsOptions?.available

    // One plain campaign budget with optional spend limits: offered by the server for new lead, sales and traffic campaigns
    const { data: campaignBudgetData } = useQuery(CAMPAIGN_BUDGET_OPTIONS, {
        variables: { campaignId, publisherId, objective: objectiveKey },
        skip:
            !campaignId ||
            !publisherId ||
            isAddAudience ||
            isEditMode ||
            !['leads', 'sales', 'traffic'].includes(objectiveKey),
        fetchPolicy: 'network-only',
    })
    const campaignBudgetOptions = campaignBudgetData?.AdCampaign?.campaignBudgetOptions || null
    // The minimum the server reports is the live one, converted to the unit of ad set budgets: without it the mode is not
    // offered, and it (never the value stored with the connection) is what the rules of a new campaign use. An edit or an
    // added audience has no such read: the server checks the minimum there and its refusal is shown.
    const liveMinimum = Number(campaignBudgetOptions?.min_daily_budget)
    const campaignBudgetOffered = !!campaignBudgetOptions?.available && Number.isInteger(liveMinimum) && liveMinimum > 0
    const plainMinimum = campaignBudgetOffered && !isEditMode && !isAddAudience ? liveMinimum : 0

    // Get audience data for filtering
    const { data: catalogueAudiencesData } = useQuery(GET_AUDIENCES)
    const audiencesData = useAudienceDataWithHypotheses(catalogueAudiencesData, getStepContentData)
    const allAudiences = audiencesData?.Audience?.audiences || []

    // Filter audience selections to exclude audiences that require pixel when pixel is inactive
    const filteredAudienceSelection = useMemo(() => {
        if (!allAudiences.length) return audienceSelection

        return Object.keys(audienceSelection).reduce((acc, stageId) => {
            const stageAudiences = audienceSelection[stageId] || []
            const validAudiences = stageAudiences.filter((audienceId) => {
                const audience = allAudiences.find((a) => a.id === audienceId)
                return audience && !isAudienceExcluded(audience, pixelIsActive)
            })
            acc[stageId] = validAudiences
            return acc
        }, {})
    }, [audienceSelection, allAudiences, pixelIsActive])
    const stagesToExclude = useMemo(() => {
        return ALL_STAGES.filter(
            (stage) => filteredAudienceSelection[stage]?.length === 0 || !availableStages.includes(stage),
        )
    }, [filteredAudienceSelection, availableStages])

    const [initialAudienceSelections] = useState(
        calculateBudgetDistribution({
            budget: 100,
            objective: selectedObjective,
            audiencesByStage: filteredAudienceSelection,
            stagesToExclude: stagesToExclude,
            hasEdits: false,
        }),
    )

    const restoreFromSavedData = (savedData) => {
        return {
            budgetType: savedData.budgetType,
            totalSmartBudget: savedData.totalSmartBudget,
            selectedObjective,
            availableStages,
            audienceSelections: savedData.audienceSelections,
            hasEdits: savedData.hasEdits,
            allowedAudiencesByStage: filteredAudienceSelection,
            manualBudgets: savedData.manualBudgets,
            minDailyBudget: minDailyBudget / 100,
            stagesToExclude,
        }
    }

    const getInitialAudienceSelections = () => {
        const savedData = getStepContentData('budgetSchedule')

        if (!_.isEmpty(savedData)) return restoreFromSavedData(savedData)

        // With a budget move the moved amount is split over the new audiences, in whole cents (whole amounts for
        // zero-decimal currencies)
        const movedSplit = budgetMove
            ? splitBudget(
                  budgetMove.movedBudget,
                  availableStages.flatMap((stage) =>
                      (filteredAudienceSelection[stage] || []).map((audience) => `${stage}:${audience}`),
                  ),
                  wholeUnitOf(currency),
              )
            : null
        const manualBudgets = {}
        availableStages.forEach((stage) => {
            const audiences = {}
            const stageAudiences = filteredAudienceSelection[stage] || []
            stageAudiences.map((audience) => {
                audiences[audience] = {
                    amount: movedSplit ? movedSplit[`${stage}:${audience}`] / 100 : minDailyBudget / 100,
                }
            })

            manualBudgets[stage] = {
                audiences,
            }
        })

        return {
            budgetType: isAddAudience ? 'manual' : 'smart',
            totalSmartBudget: 100,
            selectedObjective,
            availableStages,
            audienceSelections: initialAudienceSelections,
            allowedAudiencesByStage: filteredAudienceSelection,
            hasEdits: false,
            stagesToExclude,
            manualBudgets,
            minDailyBudget: minDailyBudget / 100,
        }
    }

    // Create the custom store with initial state - use useMemo to avoid recreating on every render
    const useCustomBudgetStore = useMemo(() => {
        return createBudgetStore(getInitialAudienceSelections())
    }, [minDailyBudget]) // Empty dependency array since initial state is static

    const store = useCustomBudgetStore()
    const adSetCount = countAudiences(filteredAudienceSelection, availableStages)
    const [campaignBudget, setCampaignBudget] = useState(
        () =>
            getStepContentData('budgetSchedule')?.campaignBudget ??
            (minDailyBudget ? String((minDailyBudget * Math.max(adSetCount, 1)) / 100) : ''),
    )
    const [adSetLimits, setAdSetLimits] = useState(() => getStepContentData('budgetSchedule')?.adSetLimits ?? {})
    // Whether the owner typed a campaign budget: until then the plain mode starts from the live minimum of its ad sets
    const campaignBudgetTyped = useRef(getStepContentData('budgetSchedule')?.campaignBudget !== undefined)
    const typeCampaignBudget = (value) => {
        campaignBudgetTyped.current = true
        setCampaignBudget(value)
    }
    const {
        budgetType,
        setBudgetType,
        totalSmartBudget,
        audienceSelections,
        setTotalSmartBudget,
        isBudgetValid,
        isBudgetLow,
        hasAnyAudienceLessBudgetThanAllowed,
        getTotalBudget,
        recalculateBudgetDistribution,
        manualBudgets,
        setStagesToExclude,
    } = store

    const [allowedAudiencesByStage] = useState(filteredAudienceSelection)

    // Update excluded stages in the budget store when they change
    useEffect(() => {
        setStagesToExclude(stagesToExclude)
    }, [stagesToExclude, setStagesToExclude])

    // Clean up wizard state when audiences are filtered out due to pixel status
    useEffect(() => {
        if (!allAudiences.length) return

        const hasFilteredOutAudiences = JSON.stringify(audienceSelection) !== JSON.stringify(filteredAudienceSelection)

        if (hasFilteredOutAudiences) {
            // Update the wizard state with filtered selections
            const salesFunnelData = getStepContentData('salesFunnelSetup') || {}
            setStepContentData(
                {
                    ...salesFunnelData,
                    audienceSelections: filteredAudienceSelection,
                },
                'salesFunnelSetup',
            )
        }
    }, [
        pixelIsActive,
        allAudiences,
        audienceSelection,
        filteredAudienceSelection,
        getStepContentData,
        setStepContentData,
    ])

    const selectBudgetType = (value) => {
        setBudgetType(value)
        // The plain mode starts from what its ad sets need at least, by the live minimum, unless a budget was typed
        if (value === CAMPAIGN_BUDGET_TYPE && !campaignBudgetTyped.current && plainMinimum > 0) {
            setCampaignBudget(String((plainMinimum * Math.max(adSetCount, 1)) / 100))
        }
    }
    const usesAdvantageLeads = budgetType === ADVANTAGE_LEADS_BUDGET_TYPE
    const usesPlainBudget = budgetType === CAMPAIGN_BUDGET_TYPE
    const usesCampaignBudget = usesAdvantageLeads || usesPlainBudget
    // The audiences (future ad sets) the limits belong to
    const limitRows = useMemo(() => {
        const stageNames = Object.fromEntries(getAllStages().map((stage) => [stage.id, stage.name]))
        return availableStages
            .filter((stage) => !stagesToExclude.includes(stage))
            .flatMap((stage) =>
                (filteredAudienceSelection[stage] || []).map((audienceId) => {
                    const audience = allAudiences.find((candidate) => candidate.id === audienceId)
                    return {
                        key: limitKey(stage, audienceId),
                        name: `${stageNames[stage] || stage}: ${audience?.name || audienceId}`,
                    }
                }),
            )
    }, [availableStages, stagesToExclude, filteredAudienceSelection, allAudiences])
    // Latest campaign budget state for the Next guard, also when an older guard of this step runs
    const campaignBudgetRef = useRef(null)
    campaignBudgetRef.current = {
        usesCampaignBudget,
        usesPlainBudget,
        offered: usesPlainBudget ? campaignBudgetOffered : advantageLeadsOffered,
        message: (usesPlainBudget ? campaignBudgetOptions?.message : advantageLeadsOptions?.message) || null,
        campaignBudget,
        adSetCount,
        limitRows,
        adSetLimits,
        plainMinimum,
    }

    // The optional warm-up and maximum daily budget belong to new campaigns with audience budgets only
    const warmupChoice = { ...defaultWarmupChoice(), ...(getStepContentData(WARMUP_STEP) || {}) }
    const offersWarmup = !isAddAudience && !isEditMode && !usesCampaignBudget && isWarmupObjective(objectiveKey)
    const audienceAmounts = []
    const budgetsByStage = budgetType === 'smart' ? audienceSelections : manualBudgets
    availableStages.forEach((stage) => {
        if (stagesToExclude.includes(stage)) return
        const allowed = allowedAudiencesByStage[stage] || []
        Object.entries(budgetsByStage?.[stage]?.audiences || {}).forEach(([audienceId, audience]) => {
            if (allowed.length > 0 && !allowed.includes(audienceId)) return
            audienceAmounts.push(parseFloat(audience.amount) || 0)
        })
    })
    // What the section found out about the feature is kept for the launch, which sends only what is offered
    const warmupOfferedRef = useRef(false)
    const reportWarmupOffered = useCallback(
        (offered) => {
            warmupOfferedRef.current = offered
            setStepContentData({ feature: offered }, WARMUP_OFFERED_STEP)
        },
        [setStepContentData],
    )
    const warmupRef = useRef(null)
    warmupRef.current = { offersWarmup, warmupChoice, audienceAmounts }

    // Save data to local storage
    useEffect(() => {
        const data = { budgetType, totalSmartBudget, audienceSelections, manualBudgets, hasEdits: store.hasEdits }

        const saved = usesCampaignBudget ? { ...data, campaignBudget } : data
        setStepContentData(usesPlainBudget || addToPlainBudget ? { ...saved, adSetLimits } : saved, 'budgetSchedule')
    }, [
        budgetType,
        totalSmartBudget,
        audienceSelections,
        setStepContentData,
        store.hasEdits,
        manualBudgets,
        usesCampaignBudget,
        usesPlainBudget,
        addToPlainBudget,
        adSetLimits,
        campaignBudget,
    ])

    // Next button validations
    useEffect(() => {
        registerNextGuard(() => {
            // Audiences added to a campaign with one campaign budget share it; their limits are still checked
            if (addToPlainBudget) {
                const limitsProblem = describeSectionProblem({
                    showBudget: false,
                    minDailyBudget: 0,
                    rows: campaignBudgetRef.current.limitRows,
                    limits: campaignBudgetRef.current.adSetLimits,
                    currency,
                })
                if (limitsProblem) {
                    showNotification(limitsProblem, 'error')
                    return false
                }
                return true
            }
            if (addToCampaignBudget) return true

            const current = campaignBudgetRef.current
            if (current.usesCampaignBudget) {
                if (!isEditMode && !current.offered) {
                    showNotification(
                        current.message ||
                            (current.usesPlainBudget
                                ? 'Campaign budgets are not available for this campaign. Choose another budget type.'
                                : 'Advantage+ leads campaigns are not available for this campaign. Choose another budget type.'),
                        'error',
                    )
                    return false
                }
                const problem = current.usesPlainBudget
                    ? describeSectionProblem({
                          campaignBudget: current.campaignBudget,
                          minDailyBudget: current.plainMinimum,
                          rows: current.limitRows,
                          limits: current.adSetLimits,
                          currency,
                      })
                    : describeCampaignBudgetProblem(
                          current.campaignBudget,
                          current.adSetCount,
                          minDailyBudget,
                          currency,
                      )
                if (problem) {
                    showNotification(problem, 'error')
                    return false
                }
                return true
            }

            const warmup = warmupRef.current
            // A choice that is hidden (feature off, read failed) never blocks Next
            if (warmup.offersWarmup && warmupOfferedRef.current) {
                const warmupProblem = describeWarmupProblem(warmup.warmupChoice, {
                    audienceAmounts: warmup.audienceAmounts,
                    minDailyBudget,
                    objective: objectiveKey,
                })
                if (warmupProblem) {
                    showNotification(warmupProblem, 'error')
                    return false
                }
            }

            if (budgetType === 'smart') {
                if (!getTotalBudget(allowedAudiencesByStage)) {
                    showNotification('Please set a total budget', 'error')
                    return false
                }

                if (hasAnyAudienceLessBudgetThanAllowed()) {
                    showNotification(
                        `Some audiences have less than the minimum budget of ${formattedMinAmount}. Please increase the budget for these audiences.`,
                        'error',
                    )
                    return false
                }
            }

            if (budgetType === 'manual') {
                if (!getTotalBudget(allowedAudiencesByStage)) {
                    showNotification('Please set a total budget', 'error')
                    return false
                }
            }

            if (
                budgetMove &&
                manualBudgetTotal(useCustomBudgetStore.getState().manualBudgets, allowedAudiencesByStage) !==
                    budgetMove.movedBudget
            ) {
                showNotification(
                    `The budgets of the new audiences must add up to ${formatCurrencyAutoLocale({
                        amount: budgetMove.movedBudget / 100,
                        currency,
                    })}, the amount moved from "${budgetMove.donorName}".`,
                    'error',
                )
                return false
            }

            if (
                budgetMove &&
                !manualBudgetsAreWhole(useCustomBudgetStore.getState().manualBudgets, allowedAudiencesByStage, currency)
            ) {
                showNotification(`Use whole amounts: ${currency} budgets have no decimals.`, 'error')
                return false
            }

            return true
        })

        return () => deregisterNextGuard()
    }, [
        budgetType,
        isBudgetValid,
        registerNextGuard,
        deregisterNextGuard,
        availableStages,
        allowedAudiencesByStage,
        getTotalBudget,
        hasAnyAudienceLessBudgetThanAllowed,
        formattedMinAmount,
        budgetMove,
        currency,
        useCustomBudgetStore,
        addToCampaignBudget,
        addToPlainBudget,
        isEditMode,
        minDailyBudget,
    ])

    // What the AI audiences can count on from one campaign budget, for the AI audience budget note
    const hypothesisAudiences = salesFunnelData?.hypothesisAudiences || {}
    const aiAdSetCount = (filteredAudienceSelection.acquisition || []).filter((id) => hypothesisAudiences[id]).length
    const aiShare = usesPlainBudget
        ? plainCampaignBudgetShare({
              campaignBudget: limitToMinor(campaignBudget).minor || 0,
              minDailyBudget: plainMinimum,
              others: limitRows
                  .filter((row) => !hypothesisAudiences[row.key.replace(/^acquisition_/, '')])
                  .map((row) => ({ minimum: limitToMinor(adSetLimits[row.key]?.min).minor || 0 })),
              ai: limitRows
                  .filter((row) => hypothesisAudiences[row.key.replace(/^acquisition_/, '')])
                  .map((row) => ({ minimum: limitToMinor(adSetLimits[row.key]?.min).minor || 0 })),
          })
        : usesAdvantageLeads
          ? campaignBudgetShare({
                campaignBudget: toMinorUnits(parseBudgetAmount(campaignBudget) || 0),
                adSetCount,
                aiAdSetCount,
                minDailyBudget,
            })
          : null

    const onTotalBudgeetChange = (value) => {
        setTotalSmartBudget(parseInt(value))
        recalculateBudgetDistribution()
    }

    return (
        <div className="tw:flex">
            <div className="tw:pb-6">
                <div className="tw:p-6">
                    {addToPlainBudget ? (
                        <div data-testid="add-to-campaign-budget">
                            <h3 className="tw:text-lg tw:font-semibold tw:mb-2">{CAMPAIGN_BUDGET_LABEL}</h3>
                            <p className="tw:text-sm tw:text-gray-200">{CAMPAIGN_BUDGET_EXISTING_NOTE}</p>
                        </div>
                    ) : addToCampaignBudget ? (
                        <div data-testid="add-to-campaign-budget">
                            <h3 className="tw:text-lg tw:font-semibold tw:mb-2">{ADVANTAGE_LEADS_LABEL}</h3>
                            <p className="tw:text-sm tw:text-gray-200">
                                This campaign has one campaign budget. The new audiences share it with the other
                                audiences, so they get no budget of their own. Change the campaign budget in Ads Manager
                                if needed. Placements and devices are automatic in this mode.
                            </p>
                        </div>
                    ) : isEditMode && usesCampaignBudget ? (
                        <div>
                            <h3 className="tw:text-lg tw:font-semibold tw:mb-2">
                                {usesPlainBudget ? CAMPAIGN_BUDGET_LABEL : ADVANTAGE_LEADS_LABEL}
                            </h3>
                        </div>
                    ) : isAddAudience ? (
                        <div>
                            <h3 className="tw:text-lg tw:font-semibold tw:mb-2">Manual budget allocation</h3>
                            <p className="tw:text-sm tw:text-gray-200">
                                Set custom budget amounts for each audience manually for full control over how your
                                budget is spent.
                            </p>
                        </div>
                    ) : (
                        <RadioGroup
                            value={budgetType}
                            onValueChange={selectBudgetType}
                            className="tw:flex tw:flex-col tw:gap-4"
                        >
                            <div className="tw:flex tw:items-start tw:gap-3">
                                <RadioGroupItem value="smart" id="smart" />
                                <div className="tw:flex tw:flex-col tw:gap-1 tw:cursor-pointer">
                                    <Label htmlFor="smart" className="tw:cursor-pointer">
                                        Smart budget distribution
                                    </Label>
                                    <Label
                                        htmlFor="smart"
                                        className="tw:text-sm tw:text-gray-200 tw:mt-1 tw:cursor-pointer"
                                    >
                                        Distribute your budget automatically across funnel stages to maximise results
                                        and efficiency based on your campaign objective and audiences.
                                    </Label>
                                </div>
                            </div>
                            <div className="tw:flex tw:items-start tw:gap-3">
                                <RadioGroupItem value="manual" id="manual" />
                                <div className="tw:flex tw:flex-col tw:gap-1 tw:cursor-pointer">
                                    <Label htmlFor="manual" className="tw:cursor-pointer">
                                        Manual budget allocation
                                    </Label>
                                    <Label
                                        htmlFor="manual"
                                        className="tw:text-sm tw:text-gray-200 tw:mt-1 tw:cursor-pointer"
                                    >
                                        Set custom budget amounts for each audience manually for full control over how
                                        your budget is spent.
                                    </Label>
                                </div>
                            </div>
                            {(advantageLeadsOffered || usesCampaignBudget) && (
                                <div className="tw:flex tw:items-start tw:gap-3">
                                    <RadioGroupItem
                                        value={ADVANTAGE_LEADS_BUDGET_TYPE}
                                        id={ADVANTAGE_LEADS_BUDGET_TYPE}
                                    />
                                    <div className="tw:flex tw:flex-col tw:gap-1 tw:cursor-pointer">
                                        <Label htmlFor={ADVANTAGE_LEADS_BUDGET_TYPE} className="tw:cursor-pointer">
                                            {ADVANTAGE_LEADS_LABEL}
                                        </Label>
                                        <Label
                                            htmlFor={ADVANTAGE_LEADS_BUDGET_TYPE}
                                            className="tw:text-sm tw:text-gray-200 tw:mt-1 tw:cursor-pointer"
                                        >
                                            One daily budget for the whole campaign. Meta shares it across the audiences
                                            and chooses the placements (Advantage+ leads).
                                        </Label>
                                        {!advantageLeadsOffered && advantageLeadsOptions?.message && (
                                            <span className="tw:text-sm tw:text-yellow-300" role="status">
                                                {advantageLeadsOptions.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {(campaignBudgetOffered || usesPlainBudget) && (
                                <div className="tw:flex tw:items-start tw:gap-3">
                                    <RadioGroupItem value={CAMPAIGN_BUDGET_TYPE} id={CAMPAIGN_BUDGET_TYPE} />
                                    <div className="tw:flex tw:flex-col tw:gap-1 tw:cursor-pointer">
                                        <Label htmlFor={CAMPAIGN_BUDGET_TYPE} className="tw:cursor-pointer">
                                            {CAMPAIGN_BUDGET_LABEL}
                                        </Label>
                                        <Label
                                            htmlFor={CAMPAIGN_BUDGET_TYPE}
                                            className="tw:text-sm tw:text-gray-200 tw:mt-1 tw:cursor-pointer"
                                        >
                                            {CAMPAIGN_BUDGET_DESCRIPTION}
                                        </Label>
                                        {!campaignBudgetOffered && campaignBudgetOptions?.message && (
                                            <span className="tw:text-sm tw:text-yellow-300" role="status">
                                                {campaignBudgetOptions.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </RadioGroup>
                    )}
                </div>
                {budgetType === 'smart' && !isAddAudience && (
                    <SmartBudgeting
                        store={store}
                        currency={currency}
                        minDailyBudget={minDailyBudget}
                        availableStages={availableStages}
                        selectedObjective={selectedObjective}
                        onBudgetChange={onTotalBudgeetChange}
                    />
                )}
                {usesPlainBudget && !isAddAudience && (
                    <CampaignBudgetSection
                        campaignBudget={campaignBudget}
                        onBudgetChange={typeCampaignBudget}
                        currency={currency}
                        minDailyBudget={plainMinimum}
                        rows={limitRows}
                        limits={adSetLimits}
                        onLimitsChange={setAdSetLimits}
                    />
                )}
                {addToPlainBudget && (
                    <CampaignBudgetSection
                        campaignBudget={campaignBudget}
                        onBudgetChange={typeCampaignBudget}
                        showBudget={false}
                        currency={currency}
                        minDailyBudget={plainMinimum}
                        rows={limitRows}
                        limits={adSetLimits}
                        onLimitsChange={setAdSetLimits}
                    />
                )}
                {usesAdvantageLeads && !isAddAudience && (
                    <AdvantageLeadsBudget
                        campaignBudget={campaignBudget}
                        onChange={typeCampaignBudget}
                        currency={currency}
                        minDailyBudget={minDailyBudget}
                        adSetCount={adSetCount}
                        minSpendShare={advantageLeadsOptions?.min_spend_share || 0}
                    />
                )}
                {(budgetType === 'manual' || isAddAudience) && !addToCampaignBudget && (
                    <ManualBudget
                        store={store}
                        currency={currency}
                        minDailyBudget={minDailyBudget}
                        selectedObjective={selectedObjective}
                        selectedAudiences={filteredAudienceSelection}
                        availableStages={availableStages}
                    />
                )}
                {offersWarmup && (
                    <WarmupSection
                        choice={warmupChoice}
                        onChange={(next) => setStepContentData(next, WARMUP_STEP)}
                        objective={objectiveKey}
                        audienceAmounts={audienceAmounts}
                        currency={currency}
                        minDailyBudget={minDailyBudget}
                        onAvailabilityChange={reportWarmupOffered}
                    />
                )}
            </div>
            <div className="tw:border-l tw:px-10 tw:py-6 tw:w-fit tw:flex-none">
                <Funnel availableStages={availableStages} audienceSelections={filteredAudienceSelection} />
                <AiAudienceBudgetNote
                    getStepContentData={getStepContentData}
                    currency={currency}
                    budgets={
                        usesCampaignBudget ? { budgetType, aiShare } : { budgetType, audienceSelections, manualBudgets }
                    }
                />
                {budgetMove && (
                    <BudgetMoveSummary
                        move={budgetMove}
                        newBudgetTotal={manualBudgetTotal(manualBudgets, allowedAudiencesByStage)}
                        currency={currency}
                    />
                )}
                {!usesCampaignBudget &&
                    !addToCampaignBudget &&
                    isBudgetLow(availableStages, allowedAudiencesByStage) && (
                        <div className="tw:text-sm tw:max-w-[222px] tw:pt-8 tw:text-yellow-300 tw:flex tw:items-center tw:gap-2 tw:flex tw:items-start">
                            <InfoIcon size={24} className="tw:min-w-6 tw:max-h-6" />
                            <span>Low budget can lead to slower learning phase and less optimised performance.</span>
                        </div>
                    )}
                {hasAnyAudienceLessBudgetThanAllowed() && budgetType === 'smart' && (
                    <div className="tw:text-sm tw:max-w-[222px] tw:pt-8 tw:text-yellow-300 tw:flex tw:items-center tw:gap-2 tw:flex tw:items-start">
                        <InfoIcon size={24} className="tw:min-w-6 tw:max-h-6" />
                        <span>
                            Some audiences have less than the minimum budget of {formattedMinAmount}. Please increase
                            the budget for these audiences.
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default BudgetSchedule
